import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Upload, Search, Bot } from "lucide-react";
import { Switch } from "../components/ui/switch";

const EMPTY = { name: "", brand: "", model: "", price: "", duration: "", warranty: "", notes: "", active: true };
const FIELDS = [
  { k: "name", label: "Serviço *", ph: "Troca de tela" },
  { k: "brand", label: "Marca", ph: "Samsung" },
  { k: "model", label: "Modelo", ph: "Galaxy A12" },
  { k: "price", label: "Preço (R$) *", ph: "250", type: "number" },
  { k: "duration", label: "Prazo", ph: "1 hora" },
  { k: "warranty", label: "Garantia", ph: "90 dias" },
];
const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]";

function Modal({ title, onClose, children, testId }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" data-testid={testId}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-display text-2xl font-bold">{title}</h2>
          <button onClick={onClose} data-testid={`${testId}-close`}><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ServiceForm({ initial, onSaved, onClose }) {
  const [f, setF] = useState(initial);
  const save = async () => {
    if (!f.name.trim() || f.price === "") return toast.error("Preencha serviço e preço");
    const body = { ...f, price: Number(f.price) };
    try {
      if (f.id) await api.patch(`/services-catalog/${f.id}`, body);
      else await api.post("/services-catalog", body);
      toast.success("Serviço salvo!");
      onSaved();
    } catch { toast.error("Erro ao salvar"); }
  };
  return (
    <Modal title={f.id ? "Editar serviço" : "Novo serviço"} onClose={onClose} testId="service-modal">
      <div className="grid grid-cols-2 gap-3">
        {FIELDS.map((x) => (
          <div key={x.k} className={x.k === "name" ? "col-span-2" : ""}>
            <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">{x.label}</label>
            <input type={x.type || "text"} value={f[x.k] ?? ""} placeholder={x.ph} data-testid={`service-input-${x.k}`}
              onChange={(e) => setF({ ...f, [x.k]: e.target.value })} className={inputCls} />
          </div>
        ))}
        <div className="col-span-2">
          <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">Observações</label>
          <textarea rows={2} value={f.notes ?? ""} data-testid="service-input-notes" placeholder="Ex: tela original, peça sob encomenda"
            onChange={(e) => setF({ ...f, notes: e.target.value })} className={inputCls} />
        </div>
      </div>
      <button onClick={save} data-testid="service-save-btn"
        className="mt-4 w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-4 py-2.5 rounded-sm font-semibold text-sm">Salvar</button>
    </Modal>
  );
}

function BulkImport({ onSaved, onClose }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/services-catalog/bulk", { text });
      toast.success(`${data.created} serviço(s) importado(s)`);
      if (data.errors.length) toast.error(`Linhas com erro: ${data.errors.join(", ")}`);
      onSaved();
    } catch { toast.error("Erro ao importar"); } finally { setBusy(false); }
  };
  return (
    <Modal title="Importar em massa" onClose={onClose} testId="service-bulk-modal">
      <p className="text-sm text-slate-500 mb-2">Uma linha por serviço, separando por <b>|</b>:</p>
      <code className="block text-xs bg-slate-50 border border-slate-200 p-2 rounded-sm mb-3 whitespace-pre">
        {"Serviço | Marca | Modelo | Preço | Prazo | Garantia | Obs\nTroca de tela | Samsung | A12 | 250 | 1h | 90 dias\nTroca de bateria | Apple | iPhone 11 | 280,00 | 40 min | 90 dias"}
      </code>
      <textarea rows={8} value={text} onChange={(e) => setText(e.target.value)} data-testid="service-bulk-text" className={inputCls} />
      <button onClick={run} disabled={busy || !text.trim()} data-testid="service-bulk-submit"
        className="mt-3 w-full bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-50 text-white px-4 py-2.5 rounded-sm font-semibold text-sm">
        {busy ? "Importando..." : "Importar"}
      </button>
    </Modal>
  );
}

export default function ServicesCatalog() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [bulk, setBulk] = useState(false);

  const load = () => api.get("/services-catalog").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const t = q.toLowerCase();
    return items.filter((i) => `${i.name} ${i.brand} ${i.model}`.toLowerCase().includes(t));
  }, [items, q]);

  const toggle = async (i, active) => { await api.patch(`/services-catalog/${i.id}`, { active }); load(); };
  const remove = async (i) => {
    if (!window.confirm(`Excluir "${i.name} ${i.model}"?`)) return;
    await api.delete(`/services-catalog/${i.id}`); load();
  };
  const done = () => { setEditing(null); setBulk(false); load(); };

  return (
    <div className="space-y-6" data-testid="services-catalog-page">
      <header className="flex flex-wrap justify-between items-end gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB] font-semibold mb-1">Assistência Técnica</div>
          <h1 className="font-display text-4xl font-bold">Tabela de Serviços</h1>
          <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
            <Bot size={14} className="text-[#2563EB]" /> A IA do WhatsApp usa os serviços ativos para passar orçamentos automaticamente.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setBulk(true)} data-testid="service-bulk-btn"
            className="border border-slate-300 hover:bg-slate-100 px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2">
            <Upload size={16} /> Importar em massa
          </button>
          <button onClick={() => setEditing(EMPTY)} data-testid="service-new-btn"
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2">
            <Plus size={16} /> Novo serviço
          </button>
        </div>
      </header>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar serviço, marca ou modelo..."
          data-testid="service-search" className={`${inputCls} pl-9`} />
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            <tr>
              {["Serviço", "Marca", "Modelo", "Preço", "Prazo", "Garantia", "Ativo"].map((h) => (
                <th key={h} className="text-left p-3">{h}</th>
              ))}
              <th className="text-right p-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((i) => (
              <tr key={i.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`service-row-${i.id}`}>
                <td className="p-3 font-medium">{i.name}</td>
                <td className="p-3">{i.brand || "—"}</td>
                <td className="p-3">{i.model || "—"}</td>
                <td className="p-3 font-semibold text-[#0A1628]">{brl(i.price)}</td>
                <td className="p-3">{i.duration || "—"}</td>
                <td className="p-3">{i.warranty || "—"}</td>
                <td className="p-3"><Switch checked={i.active} onCheckedChange={(v) => toggle(i, v)} data-testid={`service-active-${i.id}`} /></td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(i)} data-testid={`service-edit-${i.id}`} className="p-1 hover:bg-slate-200 rounded-sm mr-1"><Pencil size={14} /></button>
                  <button onClick={() => remove(i)} data-testid={`service-delete-${i.id}`} className="p-1 hover:bg-red-100 text-red-500 rounded-sm"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="p-12 text-center text-slate-500" data-testid="service-empty">Nenhum serviço cadastrado.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && <ServiceForm initial={editing} onSaved={done} onClose={() => setEditing(null)} />}
      {bulk && <BulkImport onSaved={done} onClose={() => setBulk(false)} />}
    </div>
  );
}
